// Devices whose sound is a curve: Auto Filter and the three-band EQs drawn as
// their frequency response, the compressors and the limiter as their
// transfer curve — the stage the browser plays (lib/device-graph.ts), over
// the device's own knobs.

import { useMemo } from "react";
import { deviceStage, type Stage } from "@web-shared/lib/device-graph";
import { responseDb, type BiquadSpec } from "@web-shared/lib/eq-response";
import { GenericDevice } from "@web-shared/components/devices/GenericDevice";
import type { EditorProps } from "@web-shared/components/devices/types";

const W = 220;
const H = 64;
const F_MIN = 20;
const F_MAX = 20000;

/** The biquads a filter-shaped stage plays. */
export function stageFilters(s: Stage): BiquadSpec[] | null {
  if (s.kind === "filter") {
    // The node reads a lowpass/highpass Q in dB, as the stage hands it over.
    return [{ type: s.type, frequency: s.frequency, Q: s.q, gain: 0 }];
  }
  if (s.kind === "eq3") {
    return [
      { type: "lowshelf", frequency: s.lowFreq, Q: 1, gain: s.low },
      { type: "peaking", frequency: Math.sqrt(s.lowFreq * s.highFreq), Q: 0.7, gain: s.mid },
      { type: "highshelf", frequency: s.highFreq, Q: 1, gain: s.high },
    ];
  }
  return null;
}

/** Output level for an input level through a compressor stage (soft knee). */
export function transfer(s: Extract<Stage, { kind: "compressor" }>, input: number): number {
  const over = input - s.threshold;
  const knee = s.knee;
  let out: number;
  if (2 * over < -knee) out = input;
  else if (2 * Math.abs(over) <= knee && knee > 0) out = input + ((1 / s.ratio - 1) * (over + knee / 2) ** 2) / (2 * knee);
  else out = s.threshold + over / s.ratio;
  return out + s.makeup;
}

export function CurveDevice(props: EditorProps) {
  const { device, bpm } = props;
  const stage = useMemo(() => deviceStage(device, bpm), [device, bpm]);
  const path = useMemo(() => {
    if (!stage) return null;
    const filters = stageFilters(stage);
    const pts: string[] = [];
    if (filters) {
      const range = stage.kind === "eq3" ? 24 : 30;
      for (let i = 0; i <= 120; i++) {
        const f = F_MIN * Math.pow(F_MAX / F_MIN, i / 120);
        const db = Math.max(-range, Math.min(range, responseDb(filters, f)));
        pts.push(`${((i / 120) * W).toFixed(1)},${(H / 2 - (db / range) * (H / 2 - 4)).toFixed(1)}`);
      }
      return { pts: pts.join(" "), zero: H / 2, label: stage.kind === "eq3" ? "±24 dB" : "±30 dB" };
    }
    if (stage.kind === "compressor") {
      for (let i = 0; i <= 60; i++) {
        const input = -60 + i;
        const out = Math.max(-60, Math.min(0, transfer(stage, input)));
        pts.push(`${((i / 60) * W).toFixed(1)},${(((-out) / 60) * (H - 4) + 2).toFixed(1)}`);
      }
      return { pts: pts.join(" "), zero: null, label: `${stage.threshold.toFixed(1)} dB · ${stage.ratio.toFixed(1)}:1` };
    }
    return null;
  }, [stage]);
  return (
    <div className="flex flex-col gap-2">
      {path && (
        <svg viewBox={`0 0 ${W} ${H}`} className="h-[64px] w-[220px] rounded bg-[var(--bg-deep)]" role="img" aria-label={`${device.name} curve`}>
          {path.zero !== null ? (
            <line x1={0} x2={W} y1={path.zero} y2={path.zero} stroke="var(--border-panel)" />
          ) : (
            <line x1={0} x2={W} y1={H - 2} y2={2} stroke="var(--border-panel)" strokeDasharray="2 3" />
          )}
          <polyline points={path.pts} fill="none" stroke="var(--primary)" strokeWidth={1.5} />
          <text x={W - 3} y={10} fontSize={8} textAnchor="end" fill="var(--muted-foreground)">
            {path.label}
          </text>
        </svg>
      )}
      <GenericDevice {...props} />
    </div>
  );
}
